import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from '../axios';
import { notify, confirmAction } from '../notify';
import { runAction } from '../Manage Data/manageDataShared';

// The side form used by every Manage Data page: add a record, or edit/delete the row picked in the table.
// fields: [{ name, label, entryKey, required, type: 'text' | 'textarea' | 'select', options, defaultValue, placeholder, suggestions, help }]
function EntityForm({
    noun, heading, fields, idKey, selectedEntry, setSelectedEntry, onChanged,
    insertUrl, updateUrl, deleteUrl, saveLabel = 'Save',
}) {
    const emptyForm = () => Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? '']));
    const [formData, setFormData] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const isEditMode = !!selectedEntry && !!updateUrl;

    useEffect(() => {
        if (selectedEntry) {
            setFormData(Object.fromEntries(fields.map((f) => {
                const keys = Array.isArray(f.entryKey) ? f.entryKey : [f.entryKey];
                const value = keys.map((k) => selectedEntry[k]).find((v) => v !== undefined && v !== null);
                return [f.name, value ?? f.defaultValue ?? ''];
            })));
        } else {
            setFormData(emptyForm());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntry]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const missingRequired = () => fields.find((f) => f.required && !String(formData[f.name] ?? '').trim());

    const reset = () => {
        setFormData(emptyForm());
        if (setSelectedEntry) setSelectedEntry(null);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (busy) return;
        const missing = missingRequired();
        if (missing) {
            notify.error(`${missing.label} is required.`);
            return;
        }
        setBusy(true);
        const ok = isEditMode
            ? await runAction(() => axios.put(`${window.backendUrl}/${updateUrl}/${selectedEntry[idKey]}`, formData), `${noun} updated`, `Could not update the ${noun.toLowerCase()}.`)
            : await runAction(() => axios.post(`${window.backendUrl}/${insertUrl}`, formData), `${noun} added`, `Could not add the ${noun.toLowerCase()}.`);
        setBusy(false);
        if (ok) {
            reset();
            onChanged();
        }
    };

    const handleDelete = async () => {
        if (busy) return;
        const name = formData[fields[0].name];
        const sure = await confirmAction({
            title: `Delete this ${noun.toLowerCase()}?`,
            message: name ? `“${name}” will be removed permanently.` : 'This record will be removed permanently.',
        });
        if (!sure) return;
        setBusy(true);
        const ok = await runAction(() => axios.delete(`${window.backendUrl}/${deleteUrl}/${selectedEntry[idKey]}`), `${noun} deleted`, `Could not delete the ${noun.toLowerCase()}.`);
        setBusy(false);
        if (ok) {
            reset();
            onChanged();
        }
    };

    const renderField = (f) => {
        const common = { name: f.name, value: formData[f.name] ?? '', onChange: handleInputChange, disabled: busy };
        if (f.type === 'select') {
            const options = (typeof f.options === 'function' ? f.options() : f.options)
                .map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
            // Keep an older stored value visible instead of silently switching it to the first option.
            if (common.value !== '' && !options.some((o) => String(o.value) === String(common.value))) {
                options.push({ value: common.value, label: common.value });
            }
            return (
                <Form.Select {...common}>
                    {options.map((o) => {
                        const opt = typeof o === 'object' ? o : { value: o, label: o };
                        return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                    })}
                </Form.Select>
            );
        }
        if (f.type === 'textarea') return <Form.Control as="textarea" rows={3} {...common} />;
        return (
            <>
                <Form.Control type="text" {...common} placeholder={f.placeholder} list={f.suggestions ? `${f.name}-suggestions` : undefined} />
                {f.suggestions && (
                    <datalist id={`${f.name}-suggestions`}>
                        {f.suggestions.map((s) => <option key={s} value={s} />)}
                    </datalist>
                )}
            </>
        );
    };

    return (
        <div className={`ft-form-card${isEditMode ? ' ft-editing' : ''}`}>
            <Form onSubmit={handleSubmit}>
                <h4>
                    {isEditMode ? `Edit ${noun}` : heading}
                    {isEditMode && <span className="ft-form-mode">Editing</span>}
                </h4>
                <hr />
                {fields.map((f) => (
                    <Form.Group className="mb-3" controlId={`ft-${f.name}`} key={f.name}>
                        <Form.Label>{f.label}{f.required ? ' *' : ''}</Form.Label>
                        {renderField(f)}
                        {f.help && <Form.Text muted>{f.help}</Form.Text>}
                    </Form.Group>
                ))}

                <div className="ft-form-actions">
                    {isEditMode ? (
                        <>
                            <Button variant="primary" type="submit" disabled={busy}>
                                {busy ? 'Saving…' : 'Update'}
                            </Button>
                            <Button variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
                            {deleteUrl && (
                                <Button variant="danger" className="ft-push" onClick={handleDelete} disabled={busy}>
                                    <i className="fa fa-trash-o" aria-hidden="true" style={{ marginRight: '6px' }}></i>Delete
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button variant="primary" type="submit" disabled={busy}>
                            {busy ? 'Saving…' : saveLabel}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    );
}

export default EntityForm;
